if (!customElements.get('living-room-product-selector')) {
  customElements.define(
    'living-room-product-selector',
    class LivingRoomProductSelector extends HTMLElement {
      connectedCallback() {
        this.slides = Array.from(this.querySelectorAll('[data-selector-slide]'));
        this.activeIndex = Math.max(this.slides.findIndex((slide) => slide.classList.contains('is-active')), 0);
        this.addEventListener('click', this.handleClick.bind(this));
      }

      handleClick(event) {
        const navigation = event.target.closest('[data-selector-nav]');
        if (navigation) {
          event.preventDefault();
          this.showSlide(navigation.dataset.selectorNav === 'next' ? this.activeIndex + 1 : this.activeIndex - 1);
          return;
        }

        const qtyButton = event.target.closest('[data-selector-qty]');
        if (qtyButton) {
          event.preventDefault();
          this.selectSet(qtyButton.closest('[data-selector-slide]'), qtyButton);
          return;
        }

        const buyButton = event.target.closest('[data-selector-buy]');
        if (buyButton) {
          event.preventDefault();
          this.buyNow(buyButton);
        }
      }

      showSlide(index) {
        if (!this.slides.length) return;

        this.activeIndex = (index + this.slides.length) % this.slides.length;
        this.slides.forEach((slide, slideIndex) => {
          const isActive = slideIndex === this.activeIndex;
          slide.classList.toggle('is-active', isActive);
          slide.setAttribute('aria-hidden', String(!isActive));
        });
      }

      selectSet(slide, button) {
        if (!slide || button.disabled) return;

        const setKey = String(button.dataset.set || '');
        slide.querySelectorAll('[data-selector-qty]').forEach((qtyButton) => {
          const isSelected = qtyButton === button;
          qtyButton.classList.toggle('is-selected', isSelected);
          qtyButton.setAttribute('aria-checked', String(isSelected));
        });
        slide.querySelectorAll('[data-selector-scene]').forEach((image) => {
          image.classList.toggle('is-active', image.dataset.selectorScene === setKey);
        });
        slide.querySelectorAll('[data-selector-price]').forEach((price) => {
          price.classList.toggle('is-active', price.dataset.selectorPrice === setKey);
        });

        const buyButton = slide.querySelector('[data-selector-buy]');
        if (!buyButton) return;
        buyButton.dataset.variantId = button.dataset.variantId;
        buyButton.dataset.quantity = button.dataset.quantity || '1';
        buyButton.dataset.discountCode = button.dataset.discountCode || '';
        buyButton.dataset.stackDiscountCode = button.dataset.stackDiscountCode || '';
        buyButton.disabled = false;
      }

      activeSetButton(slide) {
        return slide?.querySelector('[data-selector-qty].is-selected');
      }

      shopRoot() {
        return window.Shopify?.routes?.root || '/';
      }

      checkoutUrl() {
        return `${this.shopRoot()}checkout`;
      }

      collectDiscountCodes(setButton, buyButton) {
        const codes = [];
        const seen = new Set();
        for (const value of [
          setButton?.dataset.stackDiscountCode,
          buyButton?.dataset.stackDiscountCode,
          setButton?.dataset.discountCode,
          buyButton?.dataset.discountCode,
        ]) {
          const code = String(value || '').trim();
          const normalized = code.toLowerCase();
          if (!code || seen.has(normalized)) continue;
          seen.add(normalized);
          codes.push(code);
        }
        return codes;
      }

      async clearCart() {
        const response = await fetch(`${this.shopRoot()}cart/clear.js`, {
          ...FoxTheme.utils.fetchConfig('javascript'),
          method: 'POST',
          body: JSON.stringify({}),
        });
        if (!response.ok) {
          throw new Error(this.dataset.cartError || 'Unable to clear the cart.');
        }
        return response.json();
      }

      async applyDiscountCodes(codes) {
        if (!codes.length) return null;

        const updateConfig = FoxTheme.utils.fetchConfig('javascript');
        updateConfig.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete updateConfig.headers['Content-Type'];

        const formData = new FormData();
        formData.append('discount', codes.join(','));
        updateConfig.body = formData;

        const response = await fetch(FoxTheme.routes.cart_update_url, updateConfig);
        const result = await response.json();
        if (!response.ok || result.status) {
          throw new Error(result.description || this.dataset.discountError);
        }
        return result;
      }

      goToCheckout(codes = []) {
        const checkoutUrl = this.checkoutUrl();
        if (codes.length === 1) {
          window.location.assign(
            `${this.shopRoot()}discount/${encodeURIComponent(codes[0])}?redirect=${encodeURIComponent(checkoutUrl)}`
          );
          return;
        }
        if (codes.length > 1) {
          // Multiple codes were applied via cart/update; go straight to checkout.
          window.location.assign(checkoutUrl);
          return;
        }
        window.location.assign(checkoutUrl);
      }

      async buyNow(button) {
        const slide = button.closest('[data-selector-slide]');
        const setButton = this.activeSetButton(slide);
        const variantId = Number(setButton?.dataset.variantId || button.dataset.variantId);
        const quantity = Number(setButton?.dataset.quantity || button.dataset.quantity || 1);
        const discountCodes = this.collectDiscountCodes(setButton, button);
        if (!variantId || button.disabled || button.classList.contains('btn--loading')) return;

        const error = slide.querySelector('[data-selector-error]');
        let itemsAdded = false;

        button.classList.add('btn--loading');
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
        if (error) {
          error.hidden = true;
          error.textContent = '';
        }

        try {
          // Buy Now should checkout only the selected product, not previous cart items.
          await this.clearCart();

          const response = await fetch(FoxTheme.routes.cart_add_url, {
            ...FoxTheme.utils.fetchConfig('javascript'),
            body: JSON.stringify({
              items: [{ id: variantId, quantity }],
            }),
          });
          const addedResult = await response.json();
          if (!response.ok || addedResult.status) throw new Error(addedResult.description || this.dataset.cartError);
          itemsAdded = true;

          if (discountCodes.length > 1) {
            try {
              await this.applyDiscountCodes(discountCodes);
              window.location.assign(this.checkoutUrl());
              return;
            } catch (_discountError) {
              // Fall back to the stack code via discount URL if combining fails.
              this.goToCheckout([discountCodes[0]]);
              return;
            }
          }

          this.goToCheckout(discountCodes);
        } catch (requestError) {
          if (itemsAdded) {
            this.goToCheckout(discountCodes);
            return;
          }
          if (error) {
            error.textContent = requestError.message || this.dataset.cartError;
            error.hidden = false;
          }
          button.classList.remove('btn--loading');
          button.disabled = false;
          button.removeAttribute('aria-busy');
        }
      }
    }
  );
}
